import React, { Fragment, useState, useContext, useCallback, useEffect } from 'react';
import { Container, Card, Col, Row, CardHeader, Nav, NavLink, NavItem } from 'reactstrap';
import MainTabContent from './MainTabContent';
import classnames from 'classnames';
import styles from './Resources.module.css';
import WhatsCovered from './SalesAids/WhatsCovered/WhatsCovered';
import PlanOptions from './SalesAids/PlanOptions/PlanOptions';
import WhySell from './SalesAids/WhySell/WhySell';
import KeySellingPoints from './SalesAids/KeySellingPoints/KeySellingPoints';
import ComfortShieldprogramFaqs from './Faqs/ComfortShieldprogramFaqs';
import AdvantageProgramFaqs from './Faqs/AdvantageProgramFaqs';
import TraneClaims from './Claims/TraneClaims';
import { getBrandContent, getProfileMenus } from '../BrandingWhiteLabel/BrandContent';
import { SessionContext } from '../../context/SessionContext';
import { ReactComponent as ArrowIcon } from '../../assets/images/arrow-cheveron.svg';
import { dataLayerPush } from '../../components/Global/helpers';
import { accountDocumentTypes } from '../../components/Global/DocumentHelper';
import DealerDocumentContainer from './DealerDocumentContainer';

const ResourceContainer = () => {
    const [activeTab, setActiveTab] = useState(null);
    const [currentTab, setCurrentTab] = useState(null);
    const [navigationCheck, setNavigationCheck] = useState(false);
    const [hideContent, setHideContent] = useState(true);
    const context = useContext(SessionContext);
    const { accountName } = context;
    const currentBrand = getBrandContent(accountName);
    const menuItems = getProfileMenus(accountName);
    const accountNameLower = accountName.toLowerCase();
    const profile = context.getProfile();
    const brochureSubheading =
        'Download a PDF of the brochure and customize it with your business contact information and logo.';
    const { getCurrentTabAndSubtab, updateTab } = context;

    const { tab } = getCurrentTabAndSubtab();

    const onChangeTab = useCallback(
        (leftTab, topTab) => {
            setCurrentTab(leftTab);
            setActiveTab(topTab);
        },
        [activeTab, setActiveTab, currentTab, setCurrentTab]
    );

    useEffect(() => {
        dataLayerPush('Pageview', {
            pagePath: '/resources',
            pageTitle: 'Resources'
        });
    });

    useEffect(() => {
        if (tab.state !== undefined) {
            setActiveTab(tab.state.activeResourceTab);
            setCurrentTab(tab.state.currentResourceTab);
            setNavigationCheck(tab.state.resourceNavigationCheck);
        } else {
            menuItems?.landingResourceTab
                ? setActiveTab({ data: 'contractManagement', display: 'Contract Management' })
                : setActiveTab({ data: 'salesAids', display: 'Sales Aids' });
            setCurrentTab(0);
            setNavigationCheck(false);
        }

        if (tab.activeResourceTab) {
            setActiveTab(tab.activeResourceTab);
            tab?.currentResourceTab ? setCurrentTab(tab.currentResourceTab) : setCurrentTab(1);
        }
    }, []);

    useEffect(() => {
        updateTab({
            tabId: tab.tabId,
            tabType: 'resources',
            state: {
                activeResourceTab: activeTab,
                currentResourceTab: currentTab,
                resourceNavigationCheck: navigationCheck
            }
        });
    }, [activeTab, currentTab, navigationCheck]);

    const navTabs = {
        lennox: {
            main: [
                { data: 'salesAids', display: 'Sales Aids' },
                { data: 'contractManagement', display: 'Contract Management' },
                { data: 'marketingMaterials', display: 'Marketing Materials' },
                { data: 'claims', display: 'Claims' },
                { data: 'faqs', display: 'FAQs' }
            ],
            salesAids: [
                { title: "What's Covered", component: <WhatsCovered onClick={() => setCurrentTab(4)} /> }, // each of these should also have the corresponding content component/jsx file.
                { title: 'Plan Options', component: <PlanOptions /> },
                { title: 'Why Sell', component: <WhySell /> },
                { title: 'Key Selling Points', component: <KeySellingPoints onClick={() => setCurrentTab(4)} /> },
                {
                    title: 'Plan Pricing',
                    component: (
                        <DealerDocumentContainer
                            key={'Plan Pricing'}
                            accountDocumentType={accountDocumentTypes.PlanPricing}
                            profile={profile}
                            title={'Plan Pricing'}
                            hasScroll={true}
                        />
                    )
                }
            ],
            contractManagement: [
                {
                    title: 'Contract Transfer',
                    component: (
                        <DealerDocumentContainer
                            key={'Contract Transfer'}
                            accountDocumentType={accountDocumentTypes.ContractTransfer}
                            profile={profile}
                            title={'Contract Transfer'}
                        />
                    )
                },
                {
                    title: 'Customer Cancellation',
                    component: (
                        <DealerDocumentContainer
                            key={'Customer Cancellation'}
                            accountDocumentType={accountDocumentTypes.CustomerCancellation}
                            profile={profile}
                            title={'Customer Cancellation'}
                        />
                    )
                },
                {
                    title: 'Cancellation And Rewritten',
                    component: (
                        <DealerDocumentContainer
                            key={'Cancellation And Rewritten'}
                            accountDocumentType={accountDocumentTypes.CancellationAndRewritten}
                            profile={profile}
                            title={'Cancellation And Rewritten'}
                        />
                    )
                }
            ],
            marketingMaterials: [
                {
                    title: 'Consumer Brochure',
                    component: (
                        <DealerDocumentContainer
                            key={'Consumer Brochure'}
                            accountDocumentType={accountDocumentTypes.ConsumerBrochure}
                            profile={profile}
                            title={`Consumer Brochure ${currentBrand.programName}`}
                            subHeading={brochureSubheading}
                        />
                    )
                },
                {
                    title: 'Dealer Booklet',
                    component: (
                        <DealerDocumentContainer
                            key={'Dealer Booklet'}
                            accountDocumentType={accountDocumentTypes.DealerBrochure}
                            profile={profile}
                            title={`Dealer Booklet ${currentBrand.programName}`}
                            hasScroll={true}
                        />
                    )
                },
                {
                    title: 'Why Sell Comfort Shield',
                    component: (
                        <DealerDocumentContainer
                            key={'Why Sell'}
                            accountDocumentType={accountDocumentTypes.WhySell}
                            profile={profile}
                            title={`Why Sell ${currentBrand.programName}`}
                        />
                    )
                },
                {
                    title: 'Program Updates',
                    component: (
                        <DealerDocumentContainer
                            key={'Program Updates'}
                            accountDocumentType={accountDocumentTypes.ProgramUpdates}
                            profile={profile}
                            title={`Program Updates`}
                        />
                    )
                }
            ],
            claims: [
                {
                    title: 'Manual Claim Form',
                    component: (
                        <DealerDocumentContainer
                            key={`Manual Claim Form`}
                            accountDocumentType={accountDocumentTypes.ManualClaimForm}
                            profile={profile}
                            title={`Manual Claim Form`}
                        />
                    )
                },
                {
                    title: 'Repair Times Matrix',
                    component: (
                        <DealerDocumentContainer
                            key={`Repair Times Matrix`}
                            accountDocumentType={accountDocumentTypes.RepairTimesMatrix}
                            profile={profile}
                            title={`Repair Times Matrix`}
                            hasScroll={true}
                        />
                    )
                },
                {
                    title: 'Combined Repair Times Matrix',
                    component: (
                        <DealerDocumentContainer
                            key={`Combined Repair Times Matrix`}
                            accountDocumentType={accountDocumentTypes.RepairTimesMatrix}
                            profile={profile}
                            identifier={'Combined'}
                            title={`Combined Repair Times Matrix`}
                            hasScroll={true}
                        />
                    )
                }
            ],
            faqs: [
                { title: `${currentBrand.programName} Program FAQS`, component: <ComfortShieldprogramFaqs /> },
                {
                    title: `${currentBrand.advantageProgramName} FAQS`,
                    component: <AdvantageProgramFaqs changeTab={onChangeTab} />
                }
            ]
        },
        rheem: {
            main: [
                { data: 'salesAids', display: 'Sales Aids' },
                { data: 'contractManagement', display: 'Contract Management' },
                { data: 'marketingMaterials', display: 'Marketing Materials' },
                { data: 'claims', display: 'Claims' },
                { data: 'faqs', display: 'FAQs' }
            ],
            salesAids: [
                { title: "What's Covered", component: <WhatsCovered onClick={() => setCurrentTab(4)} /> }, // each of these should also have the corresponding content component/jsx file.
                { title: 'Plan Options', component: <PlanOptions /> },
                { title: 'Why Sell', component: <WhySell /> },
                { title: 'Key Selling Points', component: <KeySellingPoints onClick={() => setCurrentTab(4)} /> },
                {
                    title: 'Plan Pricing',
                    component: (
                        <DealerDocumentContainer
                            key={'Plan Pricing'}
                            accountDocumentType={accountDocumentTypes.PlanPricing}
                            profile={profile}
                            title={'Plan Pricing'}
                            hasScroll={true}
                        />
                    )
                }
            ],
            contractManagement: [
                {
                    title: 'Contract Transfer',
                    component: (
                        <DealerDocumentContainer
                            key={'Contract Transfer'}
                            accountDocumentType={accountDocumentTypes.ContractTransfer}
                            profile={profile}
                            title={'Contract Transfer'}
                        />
                    )
                },
                {
                    title: 'Customer Cancellation',
                    component: (
                        <DealerDocumentContainer
                            key={'Customer Cancellation'}
                            accountDocumentType={accountDocumentTypes.CustomerCancellation}
                            profile={profile}
                            title={'Customer Cancellation'}
                        />
                    )
                }
            ],
            marketingMaterials: [
                {
                    title: 'Dealer Booklet',
                    component: (
                        <DealerDocumentContainer
                            key={'Dealer Booklet'}
                            accountDocumentType={accountDocumentTypes.DealerBrochure}
                            profile={profile}
                            identifier={'Booklet'}
                            title={`Dealer Booklet ${currentBrand.programName}`}
                            hasScroll={true}
                        />
                    )
                },
                {
                    title: `Why Sell ${currentBrand.programName}`,
                    component: (
                        <DealerDocumentContainer
                            key={'Why Sell'}
                            accountDocumentType={accountDocumentTypes.WhySell}
                            profile={profile}
                            title={`Why Sell ${currentBrand.programName}`}
                        />
                    )
                }
            ],
            claims: [
                {
                    title: 'Manual Claim Form',
                    component: (
                        <DealerDocumentContainer
                            key={`Manual Claim Form`}
                            accountDocumentType={accountDocumentTypes.ManualClaimForm}
                            profile={profile}
                            title={`Manual Claim Form`}
                        />
                    )
                },
                {
                    title: 'Reimbursement Schedule',
                    component: (
                        <DealerDocumentContainer
                            key={`Reimbursement Schedule`}
                            accountDocumentType={accountDocumentTypes.ReimbursementSchedules}
                            profile={profile}
                            title={`Reimbursement Schedule`}
                        />
                    )
                },
                {
                    title: 'Core Plan Repair Hours',
                    component: (
                        <DealerDocumentContainer
                            key={`Core Plan Repair Hours`}
                            accountDocumentType={accountDocumentTypes.ClaimRepairHours}
                            profile={profile}
                            identifier={'Core'}
                            title={`Core Plan Repair Hours`}
                        />
                    )
                },
                {
                    title: 'Premium Plan Repair Codes',
                    component: (
                        <DealerDocumentContainer
                            key={`Premium Plan Repair Codes`}
                            accountDocumentType={accountDocumentTypes.ClaimRepairCodes}
                            profile={profile}
                            identifier={'Premium'}
                            title={`Premium Plan Repair Codes`}
                        />
                    )
                }
            ],
            faqs: [
                {
                    title: `${currentBrand.programName} Program FAQS`,
                    component: <ComfortShieldprogramFaqs changeTab={onChangeTab} />
                },
                {
                    title: `${currentBrand.advantageProgramName} FAQS`,
                    component: <AdvantageProgramFaqs changeTab={onChangeTab} />
                }
            ]
        },
        carrier: {
            main: [
                { data: 'salesAids', display: 'Sales Aids' },
                { data: 'contractManagement', display: 'Contract Management' },
                { data: 'marketingMaterials', display: 'Marketing Materials' },
                { data: 'claims', display: 'Claims' },
                { data: 'faqs', display: 'FAQs' }
            ],
            salesAids: [
                { title: "What's Covered", component: <WhatsCovered onClick={() => setCurrentTab(4)} /> }, // each of these should also have the corresponding content component/jsx file.
                { title: 'Plan Options', component: <PlanOptions /> },
                { title: 'Why Sell', component: <WhySell /> },
                { title: 'Key Selling Points', component: <KeySellingPoints onClick={() => setCurrentTab(4)} /> },
                {
                    title: 'Plan Pricing',
                    component: (
                        <DealerDocumentContainer
                            key={'Plan Pricing'}
                            accountDocumentType={accountDocumentTypes.PlanPricing}
                            profile={profile}
                            title={'Plan Pricing'}
                            hasScroll={true}
                        />
                    )
                }
            ],
            contractManagement: [
                {
                    title: 'Contract Transfer',
                    component: (
                        <DealerDocumentContainer
                            key={'Contract Transfer'}
                            accountDocumentType={accountDocumentTypes.ContractTransfer}
                            profile={profile}
                            title={'Contract Transfer'}
                        />
                    )
                },
                {
                    title: 'Customer Cancellation',
                    component: (
                        <DealerDocumentContainer
                            key={'Customer Cancellation'}
                            accountDocumentType={accountDocumentTypes.CustomerCancellation}
                            profile={profile}
                            title={'Customer Cancellation'}
                        />
                    )
                }
            ],
            marketingMaterials: [
                {
                    title: 'Consumer Brochure',
                    component: (
                        <DealerDocumentContainer
                            key={'Consumer Brochure'}
                            accountDocumentType={accountDocumentTypes.ConsumerBrochure}
                            profile={profile}
                            title={`Consumer Brochure ${currentBrand.programName}`}
                            subHeading={brochureSubheading}
                        />
                    )
                },
                {
                    title: 'Dealer Booklet',
                    component: (
                        <DealerDocumentContainer
                            key={'Dealer Booklet'}
                            accountDocumentType={accountDocumentTypes.DealerBrochure}
                            profile={profile}
                            identifier={'Booklet'}
                            title={`Dealer Booklet ${currentBrand.programName}`}
                            hasScroll={true}
                        />
                    )
                }
            ],
            claims: [
                {
                    title: 'Manual Claim Form',
                    component: (
                        <DealerDocumentContainer
                            key={`Manual Claim Form`}
                            accountDocumentType={accountDocumentTypes.ManualClaimForm}
                            profile={profile}
                            title={`Manual Claim Form`}
                            hasScroll={true}
                        />
                    )
                },
                {
                    title: `Manual Claim Form (${currentBrand.valuePlanName})`,
                    component: (
                        <DealerDocumentContainer
                            key={`Manual Claim Form Carrier`}
                            accountDocumentType={accountDocumentTypes.ManualClaimForm}
                            profile={profile}
                            identifier={'Prime'}
                            title={`Manual Claim Form - ${currentBrand.valuePlanName}`}
                            hasScroll={true}
                        />
                    )
                },
                {
                    title: 'Reimbursement Schedule',
                    component: (
                        <DealerDocumentContainer
                            key={`Reimbursement Schedule`}
                            accountDocumentType={accountDocumentTypes.ReimbursementSchedules}
                            profile={profile}
                            title={`Reimbursement Schedule`}
                            hasScroll={true}
                        />
                    )
                },
                {
                    title: 'Prime Plan Repair Hours',
                    component: (
                        <DealerDocumentContainer
                            key={`Prime Plan Repair Hours`}
                            accountDocumentType={accountDocumentTypes.ClaimRepairHours}
                            profile={profile}
                            identifier={'Prime'}
                            title={`Prime Plan Repair Hours`}
                        />
                    )
                }
            ],
            faqs: [
                { title: `${currentBrand.programName} Program FAQS`, component: <ComfortShieldprogramFaqs /> },
                {
                    title: `${currentBrand.advantageProgramName} FAQS`,
                    component: <AdvantageProgramFaqs changeTab={onChangeTab} />
                }
            ]
        },
        trane: {
            main: [
                { data: 'salesAids', display: 'Sales Aids' },
                { data: 'contractManagement', display: 'Contract Management' },
                { data: 'marketingMaterials', display: 'Marketing Materials' },
                { data: 'claims', display: 'Claims' },
                { data: 'faqs', display: 'FAQs' }
            ],
            salesAids: [
                { title: "What's Covered", component: <WhatsCovered onClick={() => setCurrentTab(4)} /> }, // each of these should also have the corresponding content component/jsx file.
                { title: 'Plan Options', component: <PlanOptions /> },
                { title: 'Why Sell', component: <WhySell /> },
                { title: 'Key Selling Points', component: <KeySellingPoints onClick={() => setCurrentTab(4)} /> },
                {
                    title: 'Plan Pricing',
                    component: (
                        <DealerDocumentContainer
                            key={'Plan Pricing'}
                            accountDocumentType={accountDocumentTypes.PlanPricing}
                            profile={profile}
                            title={'Plan Pricing'}
                            hasScroll={true}
                        />
                    )
                }
            ],
            contractManagement: [
                {
                    title: 'Contract Transfer',
                    component: (
                        <DealerDocumentContainer
                            key={'Contract Transfer'}
                            accountDocumentType={accountDocumentTypes.ContractTransfer}
                            profile={profile}
                            title={'Contract Transfer'}
                        />
                    )
                },
                {
                    title: 'Customer Cancellation',
                    component: (
                        <DealerDocumentContainer
                            key={'Customer Cancellation'}
                            accountDocumentType={accountDocumentTypes.CustomerCancellation}
                            profile={profile}
                            title={'Customer Cancellation'}
                        />
                    )
                }
            ],
            marketingMaterials: [
                {
                    title: 'Consumer Brochure',
                    component: (
                        <DealerDocumentContainer
                            key={'Consumer Brochure'}
                            accountDocumentType={accountDocumentTypes.ConsumerBrochure}
                            profile={profile}
                            title={`Consumer Brochure ${currentBrand.programName}`}
                            subHeading={brochureSubheading}
                        />
                    )
                },
                {
                    title: 'Dealer Booklet',
                    component: (
                        <DealerDocumentContainer
                            key={'Dealer Booklet'}
                            accountDocumentType={accountDocumentTypes.DealerBrochure}
                            profile={profile}
                            title={`Dealer Booklet ${currentBrand.programName}`}
                        />
                    )
                },
                {
                    title: `Why Sell ${currentBrand.programName}`,
                    component: (
                        <DealerDocumentContainer
                            key={'Why Sell'}
                            accountDocumentType={accountDocumentTypes.WhySell}
                            profile={profile}
                            title={`Why Sell ${currentBrand.programName}`}
                            hasScroll={true}
                        />
                    )
                }
            ],
            claims: [
                {
                    title: 'Manual Claim Form',
                    component: (
                        <DealerDocumentContainer
                            key={`Manual Claim Form`}
                            accountDocumentType={accountDocumentTypes.ManualClaimForm}
                            profile={profile}
                            title={`Manual Claim Form`}
                        />
                    )
                },
                {
                    title: 'Repair Times Matrix',
                    component: (
                        <DealerDocumentContainer
                            key={`Repair Times Matrix`}
                            accountDocumentType={accountDocumentTypes.RepairTimesMatrix}
                            profile={profile}
                            title={`Repair Times Matrix`}
                            hasScroll={true}
                        />
                    )
                },
                {
                    title: 'Reimbursement Schedule',
                    component: (
                        <DealerDocumentContainer
                            key={`Reimbursement Schedule`}
                            accountDocumentType={accountDocumentTypes.ReimbursementSchedules}
                            profile={profile}
                            title={`Reimbursement Schedule`}
                        />
                    )
                }
            ],
            faqs: [
                {
                    title: `${currentBrand.programName} Program FAQS`,
                    component: <ComfortShieldprogramFaqs changeTab={onChangeTab} />
                }
            ]
        },
        ge: {
            main: [
                { data: 'contractManagement', display: 'Contract Management' },
                { data: 'claims', display: 'Claims' }
            ],
            salesAids: [
                { title: "What's Covered", component: <WhatsCovered onClick={() => setCurrentTab(4)} /> }, // each of these should also have the corresponding content component/jsx file.
                { title: 'Plan Options', component: <PlanOptions /> },
                { title: 'Why Sell', component: <WhySell /> },
                { title: 'Key Selling Points', component: <KeySellingPoints onClick={() => setCurrentTab(4)} /> },
                {
                    title: 'Plan Pricing',
                    component: (
                        <DealerDocumentContainer
                            key={'Plan Pricing'}
                            accountDocumentType={accountDocumentTypes.PlanPricing}
                            profile={profile}
                            title={'Plan Pricing'}
                            hasScroll={true}
                        />
                    )
                }
            ],
            contractManagement: [
                {
                    title: 'Contract Transfer',
                    component: (
                        <DealerDocumentContainer
                            key={'Contract Transfer'}
                            accountDocumentType={accountDocumentTypes.ContractTransfer}
                            profile={profile}
                            title={'Contract Transfer'}
                        />
                    )
                },
                {
                    title: 'Customer Cancellation',
                    component: (
                        <DealerDocumentContainer
                            key={'Customer Cancellation'}
                            accountDocumentType={accountDocumentTypes.CustomerCancellation}
                            profile={profile}
                            title={'Customer Cancellation'}
                        />
                    )
                }
            ],
            marketingMaterials: [
                {
                    title: 'Consumer Brochure',
                    component: (
                        <DealerDocumentContainer
                            key={'Consumer Brochure'}
                            accountDocumentType={accountDocumentTypes.ConsumerBrochure}
                            profile={profile}
                            title={`Consumer Brochure ${currentBrand.programName}`}
                            subHeading={brochureSubheading}
                        />
                    )
                },
                {
                    title: 'Dealer Booklet',
                    component: (
                        <DealerDocumentContainer
                            key={'Dealer Booklet'}
                            accountDocumentType={accountDocumentTypes.DealerBrochure}
                            profile={profile}
                            title={`Dealer Booklet ${currentBrand.programName}`}
                        />
                    )
                },
                {
                    title: `Why Sell ${currentBrand.programName}`,
                    component: (
                        <DealerDocumentContainer
                            key={'Why Sell'}
                            accountDocumentType={accountDocumentTypes.WhySell}
                            profile={profile}
                            title={`Why Sell ${currentBrand.programName}`}
                            hasScroll={true}
                        />
                    )
                },
                {
                    title: 'Getting Started',
                    component: (
                        <DealerDocumentContainer
                            key={'Getting Started'}
                            accountDocumentType={accountDocumentTypes.GettingStarted}
                            profile={profile}
                            title={`Getting Started`}
                        />
                    )
                }
            ],
            claims: [
                {
                    title: 'Manual Claim Form',
                    component: (
                        <DealerDocumentContainer
                            key={`Manual Claim Form`}
                            accountDocumentType={accountDocumentTypes.ManualClaimForm}
                            profile={profile}
                            title={`Manual Claim Form`}
                        />
                    )
                },
                {
                    title: 'Repair Times Matrix',
                    component: (
                        <DealerDocumentContainer
                            key={`Repair Times Matrix`}
                            accountDocumentType={accountDocumentTypes.RepairTimesMatrix}
                            profile={profile}
                            title={`Repair Times Matrix`}
                            hasScroll={true}
                        />
                    )
                }
            ],
            faqs: [{ title: `${currentBrand.programName} Program FAQS`, component: <ComfortShieldprogramFaqs /> }]
        },
        icp: {
            main: [
                { data: 'salesAids', display: 'Sales Aids' },
                { data: 'contractManagement', display: 'Contract Management' },
                { data: 'marketingMaterials', display: 'Marketing Materials' },
                { data: 'claims', display: 'Claims' },
                { data: 'faqs', display: 'FAQS' }
            ],
            salesAids: [
                { title: "What's Covered", component: <WhatsCovered onClick={() => setCurrentTab(4)} /> }, // each of these should also have the corresponding content component/jsx file.
                { title: 'Plan Options', component: <PlanOptions /> },
                { title: 'Why Sell', component: <WhySell /> },
                { title: 'Key Selling Points', component: <KeySellingPoints onClick={() => setCurrentTab(4)} /> },
                {
                    title: 'Plan Pricing',
                    component: (
                        <DealerDocumentContainer
                            key={'Plan Pricing'}
                            accountDocumentType={accountDocumentTypes.PlanPricing}
                            profile={profile}
                            title={'Plan Pricing'}
                            hasScroll={true}
                        />
                    )
                }
            ],
            contractManagement: [
                {
                    title: 'Contract Transfer',
                    component: (
                        <DealerDocumentContainer
                            key={'Contract Transfer'}
                            accountDocumentType={accountDocumentTypes.ContractTransfer}
                            profile={profile}
                            title={'Contract Transfer'}
                        />
                    )
                },
                {
                    title: 'Customer Cancellation',
                    component: (
                        <DealerDocumentContainer
                            key={'Customer Cancellation'}
                            accountDocumentType={accountDocumentTypes.CustomerCancellation}
                            profile={profile}
                            title={'Customer Cancellation'}
                        />
                    )
                }
            ],
            marketingMaterials: [
                {
                    title: 'Consumer Brochure',
                    component: (
                        <DealerDocumentContainer
                            key={'Consumer Brochure'}
                            accountDocumentType={accountDocumentTypes.ConsumerBrochure}
                            profile={profile}
                            title={`Consumer Brochure ${currentBrand.programName}`}
                            subHeading={brochureSubheading}
                        />
                    )
                },
                {
                    title: 'Dealer Booklet',
                    component: (
                        <DealerDocumentContainer
                            key={'Dealer Booklet'}
                            accountDocumentType={accountDocumentTypes.DealerBrochure}
                            profile={profile}
                            identifier={'Booklet'}
                            title={`Dealer Booklet ${currentBrand.programName}`}
                            hasScroll={true}
                        />
                    )
                }
            ],
            claims: [
                {
                    title: 'Manual Claim Form',
                    component: (
                        <DealerDocumentContainer
                            key={`Manual Claim Form`}
                            accountDocumentType={accountDocumentTypes.ManualClaimForm}
                            profile={profile}
                            title={`Manual Claim Form`}
                        />
                    )
                },
                {
                    title: 'Manual Claim Form (Mini Split)',
                    component: (
                        <DealerDocumentContainer
                            key={`Manual Claim Form Mini Split`}
                            accountDocumentType={accountDocumentTypes.ManualClaimForm}
                            profile={profile}
                            identifier={'MiniSplit'}
                            title={`Manual Claim Form (Mini Split)`}
                        />
                    )
                },
                {
                    title: 'Manual Claim Form (Pre-May 2024)',
                    component: (
                        <DealerDocumentContainer
                            key={`Manual Claim Form pre May`}
                            accountDocumentType={accountDocumentTypes.ManualClaimForm}
                            profile={profile}
                            identifier={'OldRates'}
                            title={`Manual Claim Form (Pre-May 2024)`}
                        />
                    )
                },
                {
                    title: 'Reimbursement Schedule',
                    component: (
                        <DealerDocumentContainer
                            key={`Reimbursement Schedule`}
                            accountDocumentType={accountDocumentTypes.ReimbursementSchedules}
                            profile={profile}
                            title={`Reimbursement Schedule`}
                        />
                    )
                }
            ],
            faqs: [
                {
                    title: `${currentBrand.programName} FAQs`,
                    component: <ComfortShieldprogramFaqs changeTab={onChangeTab} />
                },
                {
                    title: `${currentBrand.advantageProgramName} FAQs`,
                    component: <AdvantageProgramFaqs changeTab={onChangeTab} />
                }
            ]
        },
        homedepot: {
            main: [
                { data: 'salesAids', display: 'Sales Aids' },
                { data: 'contractManagement', display: 'Contract Management' },
                { data: 'marketingMaterials', display: 'Marketing Materials' },
                { data: 'claims', display: 'Claims' },
                { data: 'faqs', display: 'FAQS' }
            ],
            salesAids: [
                { title: "What's Covered", component: <WhatsCovered onClick={() => setCurrentTab(4)} /> }, // each of these should also have the corresponding content component/jsx file.
                { title: 'Plan Options', component: <PlanOptions /> }, // Commenting out until branding is implemented
                { title: 'Why Sell', component: <WhySell /> },
                { title: 'Key Selling Points', component: <KeySellingPoints onClick={() => setCurrentTab(4)} /> },
                {
                    title: 'Plan Pricing',
                    component: (
                        <DealerDocumentContainer
                            key={'Plan Pricing'}
                            accountDocumentType={accountDocumentTypes.PlanPricing}
                            profile={profile}
                            title={'Plan Pricing'}
                            hasScroll={true}
                        />
                    )
                }
            ],
            contractManagement: [
                {
                    title: 'Contract Transfer',
                    component: (
                        <DealerDocumentContainer
                            key={'Contract Transfer'}
                            accountDocumentType={accountDocumentTypes.ContractTransfer}
                            profile={profile}
                            title={'Contract Transfer'}
                        />
                    )
                },
                {
                    title: 'Customer Cancellation',
                    component: (
                        <DealerDocumentContainer
                            key={'Customer Cancellation'}
                            accountDocumentType={accountDocumentTypes.CustomerCancellation}
                            profile={profile}
                            title={'Customer Cancellation'}
                        />
                    )
                }
            ],
            marketingMaterials: [
                {
                    title: 'Consumer Brochure',
                    component: (
                        <DealerDocumentContainer
                            key={'Consumer Brochure'}
                            accountDocumentType={accountDocumentTypes.ConsumerBrochure}
                            profile={profile}
                            title={`Consumer Brochure ${currentBrand.programName}`}
                            subHeading={brochureSubheading}
                        />
                    )
                },
                {
                    title: 'Dealer Booklet',
                    component: (
                        <DealerDocumentContainer
                            key={'Dealer Booklet'}
                            accountDocumentType={accountDocumentTypes.DealerBrochure}
                            profile={profile}
                            identifier={'Booklet'}
                            title={`Dealer Booklet ${currentBrand.programName}`}
                            hasScroll={true}
                        />
                    )
                },
                {
                    title: `Why Sell ${currentBrand.programName} `,
                    component: (
                        <DealerDocumentContainer
                            key={'Why Sell'}
                            accountDocumentType={accountDocumentTypes.WhySell}
                            profile={profile}
                            title={`Why Sell ${currentBrand.programName}`}
                        />
                    )
                },
                {
                    title: 'Program Updates',
                    component: (
                        <DealerDocumentContainer
                            key={'Program Updates'}
                            accountDocumentType={accountDocumentTypes.ProgramUpdates}
                            profile={profile}
                            title={`Program Updates`}
                        />
                    )
                }
            ],
            claims: [
                {
                    title: 'Manual Claim Form',
                    component: (
                        <DealerDocumentContainer
                            key={`Manual Claim Form`}
                            accountDocumentType={accountDocumentTypes.ManualClaimForm}
                            profile={profile}
                            title={`Manual Claim Form`}
                        />
                    )
                },
                {
                    title: 'Reimbursement Schedule',
                    component: (
                        <DealerDocumentContainer
                            key={`Reimbursement Schedule`}
                            accountDocumentType={accountDocumentTypes.ReimbursementSchedules}
                            profile={profile}
                            title={`Reimbursement Schedule`}
                        />
                    )
                }
            ],
            faqs: [
                {
                    title: `${currentBrand.programName} FAQs`,
                    component: <ComfortShieldprogramFaqs changeTab={onChangeTab} />
                },
                {
                    title: `${currentBrand.advantageProgramName} FAQs`,
                    component: <AdvantageProgramFaqs changeTab={onChangeTab} />
                }
            ]
        },
        mcc: {
            main: [
                { data: 'salesAids', display: 'Sales Aids' },
                { data: 'contractManagement', display: 'Contract Management' },
                { data: 'marketingMaterials', display: 'Marketing Materials' },
                { data: 'claims', display: 'Claims' },
                { data: 'faqs', display: 'FAQS' }
            ],
            salesAids: [
                { title: "What's Covered", component: <WhatsCovered onClick={() => setCurrentTab(4)} /> }, // each of these should also have the corresponding content component/jsx file.
                { title: 'Plan Options', component: <PlanOptions /> }, // Commenting out until branding is implemented
                { title: 'Why Sell', component: <WhySell /> },
                { title: 'Key Selling Points', component: <KeySellingPoints onClick={() => setCurrentTab(4)} /> },
                {
                    title: 'Plan Pricing',
                    component: (
                        <DealerDocumentContainer
                            key={'Plan Pricing'}
                            accountDocumentType={accountDocumentTypes.PlanPricing}
                            profile={profile}
                            title={'Plan Pricing'}
                            hasScroll={true}
                        />
                    )
                }
            ],
            contractManagement: [
                {
                    title: 'Contract Transfer',
                    component: (
                        <DealerDocumentContainer
                            key={'Contract Transfer'}
                            accountDocumentType={accountDocumentTypes.ContractTransfer}
                            profile={profile}
                            title={'Contract Transfer'}
                        />
                    )
                },
                {
                    title: 'Customer Cancellation',
                    component: (
                        <DealerDocumentContainer
                            key={'Customer Cancellation'}
                            accountDocumentType={accountDocumentTypes.CustomerCancellation}
                            profile={profile}
                            title={'Customer Cancellation'}
                        />
                    )
                }
            ],
            marketingMaterials: [
                {
                    title: 'Dealer Booklet',
                    component: (
                        <DealerDocumentContainer
                            key={'Dealer Booklet'}
                            accountDocumentType={accountDocumentTypes.DealerBrochure}
                            profile={profile}
                            identifier={'Booklet'}
                            title={`Dealer Booklet ${currentBrand.programName}`}
                            hasScroll={true}
                        />
                    )
                },
                {
                    title: `Why Sell ${currentBrand.programName} `,
                    component: (
                        <DealerDocumentContainer
                            key={'Why Sell'}
                            accountDocumentType={accountDocumentTypes.WhySell}
                            profile={profile}
                            title={`Why Sell ${currentBrand.programName}`}
                        />
                    )
                }
            ],
            claims: [
                {
                    title: 'Manual Claim Form',
                    component: (
                        <DealerDocumentContainer
                            key={`Manual Claim Form`}
                            accountDocumentType={accountDocumentTypes.ManualClaimForm}
                            profile={profile}
                            title={`Manual Claim Form`}
                        />
                    )
                },
                {
                    title: 'Reimbursement Schedule',
                    component: (
                        <DealerDocumentContainer
                            key={`Reimbursement Schedule`}
                            accountDocumentType={accountDocumentTypes.ReimbursementSchedules}
                            profile={profile}
                            title={`Reimbursement Schedule`}
                        />
                    )
                }
            ],
            faqs: [{ title: `${currentBrand.programName} FAQs`, component: <ComfortShieldprogramFaqs /> }]
        },
        nordyne: {
            main: [
                { data: 'salesAids', display: 'Sales Aids' },
                { data: 'contractManagement', display: 'Contract Management' },
                { data: 'marketingMaterials', display: 'Marketing Materials' },
                { data: 'claims', display: 'Claims' },
                { data: 'faqs', display: 'FAQS' }
            ],
            salesAids: [
                { title: "What's Covered", component: <WhatsCovered onClick={() => setCurrentTab(4)} /> }, // each of these should also have the corresponding content component/jsx file.
                { title: 'Plan Options', component: <PlanOptions /> }, // Commenting out until branding is implemented
                { title: 'Why Sell', component: <WhySell /> },
                { title: 'Key Selling Points', component: <KeySellingPoints onClick={() => setCurrentTab(4)} /> },
                {
                    title: 'Plan Pricing',
                    component: (
                        <DealerDocumentContainer
                            key={'Plan Pricing'}
                            accountDocumentType={accountDocumentTypes.PlanPricing}
                            profile={profile}
                            title={'Plan Pricing'}
                            hasScroll={true}
                        />
                    )
                }
            ],
            contractManagement: [
                {
                    title: 'Contract Transfer',
                    component: (
                        <DealerDocumentContainer
                            key={'Contract Transfer'}
                            accountDocumentType={accountDocumentTypes.ContractTransfer}
                            profile={profile}
                            title={'Contract Transfer'}
                        />
                    )
                },
                {
                    title: 'Customer Cancellation',
                    component: (
                        <DealerDocumentContainer
                            key={'Customer Cancellation'}
                            accountDocumentType={accountDocumentTypes.CustomerCancellation}
                            profile={profile}
                            title={'Customer Cancellation'}
                        />
                    )
                }
            ],
            marketingMaterials: [
                {
                    title: 'Dealer Booklet',
                    component: (
                        <DealerDocumentContainer
                            key={'Dealer Booklet'}
                            accountDocumentType={accountDocumentTypes.DealerBrochure}
                            profile={profile}
                            identifier={'Booklet'}
                            title={`Dealer Booklet ${currentBrand.programName}`}
                            hasScroll={true}
                        />
                    )
                },
                {
                    title: `Why Sell ${currentBrand.programName} `,
                    component: (
                        <DealerDocumentContainer
                            key={'Why Sell'}
                            accountDocumentType={accountDocumentTypes.WhySell}
                            profile={profile}
                            title={`Why Sell ${currentBrand.programName}`}
                        />
                    )
                }
            ],
            claims: [
                {
                    title: 'Manual Claim Form',
                    component: (
                        <DealerDocumentContainer
                            key={`Manual Claim Form`}
                            accountDocumentType={accountDocumentTypes.ManualClaimForm}
                            profile={profile}
                            title={`Manual Claim Form`}
                        />
                    )
                },
                {
                    title: 'Reimbursement Schedule',
                    component: (
                        <DealerDocumentContainer
                            key={`Reimbursement Schedule`}
                            accountDocumentType={accountDocumentTypes.ReimbursementSchedules}
                            profile={profile}
                            title={`Reimbursement Schedule`}
                        />
                    )
                }
            ],
            faqs: [
                {
                    title: `${currentBrand.programName} FAQs`,
                    component: <ComfortShieldprogramFaqs changeTab={onChangeTab} />
                }
            ]
        },
        staples: {
            main: [
                { data: 'salesAids', display: 'Sales Aids' },
                { data: 'marketingMaterials', display: 'Marketing Materials' },
                { data: 'faqs', display: 'FAQS' }
            ],
            salesAids: [
                {
                    title: 'Program Comparison',
                    component: (
                        <DealerDocumentContainer
                            key={'Program Comparison'}
                            accountDocumentType={accountDocumentTypes.ProgramComparision}
                            profile={profile}
                            title={`Program Comparison`}
                            hasScroll={true}
                        />
                    )
                },
                {
                    title: 'Why Sell',
                    component: (
                        <DealerDocumentContainer
                            key={'Why Sell'}
                            accountDocumentType={accountDocumentTypes.WhySell}
                            profile={profile}
                            title={`Why Sell`}
                        />
                    )
                }
            ],
            marketingMaterials: [
                {
                    title: 'Consumer Brochure',
                    component: (
                        <DealerDocumentContainer
                            key={'Consumer Brochure'}
                            accountDocumentType={accountDocumentTypes.ConsumerBrochure}
                            profile={profile}
                            identifier={'Brochure'}
                            title={`Consumer Brochure`}
                            hasScroll={true}
                        />
                    )
                },
                {
                    title: 'Program Overview',
                    component: (
                        <DealerDocumentContainer
                            key={'Program Overview'}
                            accountDocumentType={accountDocumentTypes.ProgramOverview}
                            profile={profile}
                            title={`Program Overview`}
                        />
                    )
                }
            ],
            faqs: [
                {
                    title: 'FAQs',
                    component: (
                        <DealerDocumentContainer
                            key={'FAQs'}
                            accountDocumentType={accountDocumentTypes.StaplesFaqs}
                            profile={profile}
                            title={`FAQs`}
                        />
                    )
                }
            ]
        },
        mainline: {
            main: [
                { data: 'salesAids', display: 'Sales Aids' },
                { data: 'contractManagement', display: 'Contract Management' },
                { data: 'marketingMaterials', display: 'Marketing Materials' },
                { data: 'claims', display: 'Claims' },
                { data: 'faqs', display: 'FAQs' }
            ],
            salesAids: [
                { title: "What's Covered", component: <WhatsCovered onClick={() => setCurrentTab(4)} /> }, // each of these should also have the corresponding content component/jsx file.
                { title: 'Plan Options', component: <PlanOptions /> },
                { title: 'Why Sell', component: <WhySell /> },
                { title: 'Key Selling Points', component: <KeySellingPoints onClick={() => setCurrentTab(4)} /> },
                {
                    title: 'Plan Pricing',
                    component: (
                        <DealerDocumentContainer
                            key={'Plan Pricing'}
                            accountDocumentType={accountDocumentTypes.PlanPricing}
                            profile={profile}
                            title={'Plan Pricing'}
                            hasScroll={true}
                        />
                    )
                }
            ],
            contractManagement: [
                {
                    title: 'Contract Transfer',
                    component: (
                        <DealerDocumentContainer
                            key={'Contract Transfer'}
                            accountDocumentType={accountDocumentTypes.ContractTransfer}
                            profile={profile}
                            title={'Contract Transfer'}
                        />
                    )
                },
                {
                    title: 'Customer Cancellation',
                    component: (
                        <DealerDocumentContainer
                            key={'Customer Cancellation'}
                            accountDocumentType={accountDocumentTypes.CustomerCancellation}
                            profile={profile}
                            title={'Customer Cancellation'}
                        />
                    )
                }
            ],
            marketingMaterials: [
                {
                    title: 'Dealer Booklet',
                    component: (
                        <DealerDocumentContainer
                            key={'Dealer Booklet'}
                            accountDocumentType={accountDocumentTypes.DealerBrochure}
                            profile={profile}
                            identifier={'Booklet'}
                            title={`Dealer Booklet ${currentBrand.programName}`}
                            hasScroll={true}
                        />
                    )
                },
                {
                    title: `Why Sell ${currentBrand.programName}`,
                    component: (
                        <DealerDocumentContainer
                            key={'Why Sell'}
                            accountDocumentType={accountDocumentTypes.WhySell}
                            profile={profile}
                            title={`Why Sell ${currentBrand.programName}`}
                            hasScroll={true}
                        />
                    )
                }
            ],
            claims: [
                {
                    title: 'Manual Claim Form',
                    component: (
                        <DealerDocumentContainer
                            key={`Manual Claim Form`}
                            accountDocumentType={accountDocumentTypes.ManualClaimForm}
                            profile={profile}
                            title={`Manual Claim Form`}
                        />
                    )
                },
                {
                    title: 'Reimbursement Schedule',
                    component: (
                        <DealerDocumentContainer
                            key={`Reimbursement Schedule`}
                            accountDocumentType={accountDocumentTypes.ReimbursementSchedules}
                            profile={profile}
                            title={`Reimbursement Schedule`}
                        />
                    )
                },
                {
                    title: 'Plan Repair Codes',
                    component: (
                        <DealerDocumentContainer
                            key={`Plan Repair Codes`}
                            accountDocumentType={accountDocumentTypes.ClaimRepairCodes}
                            profile={profile}
                            title={`Plan Repair Codes`}
                        />
                    )
                }
            ],
            faqs: [
                {
                    title: `${currentBrand.programName} FAQs`,
                    component: <ComfortShieldprogramFaqs changeTab={onChangeTab} />
                }
            ]
        }
    };

    const setMobileNavigation = (val) => {
        setNavigationCheck(val);
    };

    const handleMobileContent = () => {
        setHideContent(false);
    };

    return (
        <div className='d-content'>
            {activeTab !== null && (
                <main
                    role='main'
                    className='mainWrapper'
                >
                    <Container className='contentWrapper'>
                        <Card className='d-none d-sm-block'>
                            <div
                                className='mt-2'
                                id={styles.topNav}
                            >
                                <Nav
                                    tabs
                                    role='tablist'
                                    className={classnames(styles.nav)}
                                >
                                    {navTabs[accountNameLower].main.map((navTab, index) => (
                                        <ul
                                            className={classnames(
                                                'mt-3',
                                                { ul: true },
                                                navTab.data === activeTab.data ? styles.ActiveTopNav : ''
                                            )}
                                            key={index}
                                        >
                                            <li
                                                onClick={() => {
                                                    setCurrentTab(0);
                                                    setActiveTab(navTab);
                                                }}
                                            >
                                                {navTab.display}
                                            </li>
                                        </ul>
                                    ))}
                                </Nav>
                            </div>
                            <MainTabContent
                                sideNavTabs={navTabs[accountNameLower]}
                                mainTab={activeTab}
                                activeTab={currentTab}
                                onTabChange={setCurrentTab}
                            />
                        </Card>
                        <Card className='d-block d-sm-none'>
                            {navigationCheck && (
                                <Row>
                                    <Col
                                        onClick={() => {
                                            setMobileNavigation(false);
                                            setHideContent(true);
                                        }}
                                        className='pt-3 pb-1 px-4 ml-2 ml-md-0 media'
                                    >
                                        <span className={classnames(styles.iconContainer, 'mr-1')}>
                                            <ArrowIcon />
                                        </span>
                                        <h1 className='h6 mt-1'>Resources</h1>
                                    </Col>
                                </Row>
                            )}
                            {!navigationCheck && (
                                <div className={classnames(styles.sideNav)}>
                                    <ul>
                                        {navTabs[accountNameLower].main.map((navTab, index) => (
                                            <li
                                                className={classnames(
                                                    index === activeTab ? styles.activeSideTab : '',
                                                    'm'
                                                )}
                                                key={index}
                                                onClick={() => {
                                                    setCurrentTab(0);
                                                    setActiveTab(navTab);
                                                    setMobileNavigation(true);
                                                }}
                                            >
                                                {navTab.display}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <MainTabContent
                                sideNavTabs={navTabs[accountNameLower]}
                                mainTab={activeTab}
                                activeTab={currentTab}
                                onTabChange={setCurrentTab}
                                navigationCheck={navigationCheck}
                                hideContent={hideContent}
                                handleMobileContent={handleMobileContent}
                            />
                        </Card>
                    </Container>
                </main>
            )}
        </div>
    );
};
export default ResourceContainer;
